/**
 * IftAboutPage — "About the archive" page for indianfamilytree.com.
 *
 * A quiet, respectful narrative telling visitors what IFT is, who built
 * it, and how it differs from Ancestry/MyHeritage. Uses the same
 * historic aesthetic tokens as the homepage.
 */
import { Link } from "react-router-dom";
import { TreePine, Feather, ScrollText, Users, Heart, ArrowRight } from "lucide-react";
import SeoHead from "@/components/SeoHead";

const P = {
  bg:      "#F5EAD3",
  bgDeep:  "#E8D9B5",
  ink:     "#3A1F0F",
  inkSoft: "#6B4A2E",
  saffron: "#B25D38",
  gold:    "#C08A2E",
  tulsi:   "#0E7C55",
  ivory:   "#FFF8E6",
};

function Ornament() {
  return (
    <div className="flex items-center gap-3 my-6" aria-hidden>
      <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, transparent, ${P.gold}55, ${P.gold}, ${P.gold}55, transparent)` }} />
      <span className="text-lg" style={{ color: P.gold }}>❦</span>
      <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, transparent, ${P.gold}55, ${P.gold}, ${P.gold}55, transparent)` }} />
    </div>
  );
}

const VALUES = [
  { Icon: Heart,      title: "Respect",      body: "Every ancestor is remembered on their own terms — in the language, script, and calendar their family used." },
  { Icon: Feather,    title: "Craft",        body: "We build software the way our grandmothers wrote letters — deliberately, unhurried, on good paper." },
  { Icon: ScrollText, title: "Longevity",    body: "Your family archive should outlive your login. Data is exportable, portable, yours." },
  { Icon: Users,      title: "Community",    body: "Cousin-discovery is opt-in and gentle. No auto-friending, no growth-hack notifications." },
];

export default function IftAboutPage() {
  return (
    <div style={{ background: P.bg, color: P.ink, minHeight: "100vh" }} data-testid="ift-about">
      <SeoHead
        section="about"
        title="About — Indian Family Tree"
        description="Why we built IndianFamilyTree.com, how we differ from other genealogy platforms, and the principles that guide the archive."
        url="https://indianfamilytree.com/about"
      />

      {/* Hero */}
      <section className="relative overflow-hidden py-16 lg:py-20" style={{ background: P.bgDeep }}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="inline-flex items-center gap-2 rounded-full border-2 px-3 py-1 text-[10px] font-mono uppercase tracking-widest"
               style={{ borderColor: P.saffron, color: P.saffron, background: P.ivory }}>
            <TreePine className="w-3 h-3" /> About the archive
          </div>
          <h1 className="mt-4 font-display italic text-4xl sm:text-6xl leading-[1.05]" style={{ color: P.ink }}>
            A quieter place <br />
            <span style={{ color: P.saffron }}>to remember our families.</span>
          </h1>
        </div>
      </section>

      {/* Narrative */}
      <section className="max-w-3xl mx-auto px-4 py-16">
        <div className="prose prose-lg" style={{ color: P.ink }}>
          <p className="font-display italic text-2xl leading-relaxed" style={{ color: P.ink }}>
            IndianFamilyTree.com began with a small, stubborn question — why do global genealogy platforms treat Indian families as an afterthought?
          </p>
          <Ornament />
          <p style={{ color: P.inkSoft }}>
            The answer, we learnt, was structural. Western pedigree trees assume a single surname line, a nuclear family, a fixed birth-death arithmetic. Indian families are matrilineal in Kerala, gotra-organised in the north, tharavadu-anchored on the Malabar coast, joint-family in Rajasthan, and diasporic across seven continents. The shape of an Indian family is a lattice, not a line.
          </p>
          <p style={{ color: P.inkSoft }}>
            So we built a platform that treats every branch as first-class. Add a spouse's parents. Add a step-grand-uncle's second marriage. Add the great-aunt in Detroit and the cousin in Doha. Add the tenant farmer great-grandfather who signed with a fingerprint on a Portuguese land record from 1892. Every ancestor deserves a page. Every page deserves respect.
          </p>
          <p style={{ color: P.inkSoft }}>
            IndianFamilyTree is part of the Social Pulse network — a family of purpose-built platforms sharing one identity, one archive, and one respectful philosophy. When you build a tree here, your cousins can find you on FoodPulse. Your grandfather's memorial candle burns on MyFamilyTree.care. Your Kerala village page connects to Kerala.com's landscape archive. It is one archive, gently interwoven.
          </p>
          <p style={{ color: P.inkSoft }}>
            We are a small team based in Kerala and the diaspora, and we build slowly. That is a feature.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="py-16" style={{ background: P.ivory, borderTop: `1px solid ${P.gold}55`, borderBottom: `1px solid ${P.gold}55` }}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center">
            <div className="text-[11px] font-mono uppercase tracking-[0.3em]" style={{ color: P.gold }}>
              Four commitments
            </div>
            <h2 className="mt-2 font-display italic text-4xl" style={{ color: P.ink }}>What we hold true.</h2>
          </div>
          <Ornament />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v) => (
              <div key={v.title} className="p-5 rounded-lg" style={{ background: P.bg, borderLeft: `4px solid ${P.saffron}` }}>
                <v.Icon className="w-6 h-6 mb-2" style={{ color: P.saffron }} />
                <h3 className="font-display text-xl" style={{ color: P.ink }}>{v.title}</h3>
                <p className="mt-1 text-sm" style={{ color: P.inkSoft }}>{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sister sites */}
      <section className="max-w-4xl mx-auto px-4 py-16">
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-center" style={{ color: P.gold }}>
          Part of the Social Pulse network
        </div>
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {[
            { name: "Social Pulse",        href: "https://socialpulse.com",        note: "The parent platform — dashboards, businesses, users." },
            { name: "Food Pulse",          href: "https://foodpulse.net",          note: "Food heritage, recipes, restaurant directory." },
            { name: "MyFamilyTree.care",   href: "https://myfamilytree.care",      note: "The global family-tree platform." },
            { name: "Kerala.com",          href: "https://kerala.com",             note: "Kerala's landscape, culture, and business archive." },
            { name: "Nature Pulse",        href: "https://naturepulse.cloud",      note: "Environment & wildlife pulse of the subcontinent." },
            { name: "Indias.com",          href: "https://indias.com",             note: "The Indian family hub across the diaspora." },
          ].map((s) => (
            <a key={s.name} href={s.href} target="_blank" rel="noreferrer"
              className="block p-4 rounded-lg hover:shadow-md transition-shadow border-2"
              style={{ borderColor: `${P.gold}44`, background: P.ivory }}>
              <div className="flex items-center gap-2">
                <span className="font-display text-lg" style={{ color: P.ink }}>{s.name}</span>
                <ArrowRight className="w-3.5 h-3.5 ml-auto opacity-60" />
              </div>
              <div className="text-xs mt-1 italic" style={{ color: P.inkSoft }}>{s.note}</div>
            </a>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 text-center">
        <div className="text-3xl" style={{ color: P.gold }}>❦</div>
        <h2 className="mt-3 font-display italic text-3xl" style={{ color: P.ink }}>Begin your family's chapter.</h2>
        <div className="mt-5 flex flex-wrap gap-3 justify-center">
          <Link to="/family-trees/new" className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-white font-semibold" style={{ background: P.saffron }}>
            <TreePine className="w-4 h-4" /> Start my family tree
          </Link>
          <Link to="/contact" className="inline-flex items-center gap-2 rounded-full px-6 py-3 border-2 font-semibold" style={{ borderColor: P.ink, color: P.ink }}>
            Say hello
          </Link>
        </div>
      </section>
    </div>
  );
}
